import { useMemo, useEffect } from 'react';
import { Avatar, Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components/datagrid/Custom';
import MoreMenuButton from 'components/util/MoreMenuButton';
import DisabledIcon from 'components/util/Status/DisabledIcon';
import EnabledIcon from 'components/util/Status/EnabledIcon';
import { DataGridKey } from 'redux/slices/datagrid';
import { RootState, useSelector } from 'redux/store';
import { PATH_DASHBOARD } from 'routes/paths';
import { useNavigate } from 'react-router';
import { NpsNotification } from '../../@types/npsNotification';
import useCustomDispatch from 'redux/dispatch';
import {
  cloneNpsNotiofication,
  deleteNpsNotification,
  getNpsNotifications
} from 'redux/slices/npsNotifications';
import { useTranslator } from 'translation/useTranslator';

let thumbSize = 48;
const DATA_GRID_KEY = DataGridKey.NpsNotifications;

const NpsNotifications = () => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const { npsNotifications, isLoading } = useSelector((state: RootState) => state.npsNotifications);
  const navigate = useNavigate();
  const { density } = useSelector((state: RootState) => state.datagrid);

  useEffect(() => {
    customDispatch({
      action: getNpsNotifications,
      disableSuccessMessage: true
    });
  }, [customDispatch]);

  useEffect(() => {
    if (density[DATA_GRID_KEY] === 'compact') {
      thumbSize = 30;
    } else if (density[DATA_GRID_KEY] === 'standard') {
      thumbSize = 46;
    } else if (density[DATA_GRID_KEY] === 'comfortable') {
      thumbSize = 62;
    }
  }, [density]);

  const handleNaviagteToNpsNotification = useMemo(
    () => (notificationId: string, isDefault: boolean) => {
      navigate(
        `${PATH_DASHBOARD.npsNotifications.edit.replace(
          ':id',
          notificationId ?? ''
        )}?isDefault=${isDefault}`
      );
    },
    [navigate]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'id'
      },
      {
        field: 'logoUrl',
        headerName: translator.logo(),
        headerAlign: 'center',
        width: 120,
        align: 'center',
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          const url = params.row.logoUrl as string;
          if (url) {
            return (
              <Avatar
                imgProps={{ sx: { objectFit: 'contain' } }}
                variant="square"
                src={url}
                sx={{ width: thumbSize, height: thumbSize }}
              />
            );
          }
        }
      },
      {
        field: 'name',
        headerName: translator.name(),
        flex: 1
      },
      {
        field: 'subject',
        headerName: translator.subject(),
        flex: 1
      },
      {
        field: 'previewText',
        headerName: translator.previewText(),
        flex: 1
      },
      {
        field: 'isDefault',
        headerName: translator.editable(),
        type: 'boolean',
        flex: 1,
        renderCell: (params) => {
          const isDefault = params.row.isDefault as boolean;
          return isDefault ? (
            <DisabledIcon tooltip={translator.managedByNpsToday()} />
          ) : (
            <EnabledIcon tooltip={translator.customNotification()} />
          );
        }
      },
      {
        field: 'edit',
        headerName: '',
        editable: false,
        disableColumnMenu: true,
        hideable: false,
        align: 'center',
        hideSortIcons: true,
        filterable: false,
        sortable: false,
        renderCell: (cellValues) => {
          const isDefault = cellValues.row.isDefault as boolean;
          const npsNotification = cellValues.row as NpsNotification;
          const notificationId = cellValues.id as string;
          return (
            <MoreMenuButton
              itemType={translator.npsNotification()}
              onDisplay={
                isDefault
                  ? () => handleNaviagteToNpsNotification(notificationId, isDefault)
                  : undefined
              }
              onEdit={
                isDefault
                  ? undefined
                  : () => handleNaviagteToNpsNotification(notificationId, isDefault)
              }
              onDelete={
                isDefault
                  ? undefined
                  : async () =>
                      customDispatch({
                        action: deleteNpsNotification,
                        actionParameters: { npsNotificationId: notificationId },
                        onSuccess: () => {
                          customDispatch({
                            action: getNpsNotifications,
                            disableSuccessMessage: true
                          });
                        }
                      })
              }
              onClone={() => {
                customDispatch({
                  action: cloneNpsNotiofication,
                  actionParameters: { npsNotification: npsNotification },
                  disableSuccessMessage: true
                });

                navigate(PATH_DASHBOARD.npsNotifications.new);
              }}
            />
          );
        }
      }
    ],
    [translator, handleNaviagteToNpsNotification, customDispatch, navigate]
  );

  return (
    <Box>
      <CustomDataGrid
        dataGridKey={DATA_GRID_KEY}
        autoHeight
        loading={isLoading.getNpsNotifications}
        pagination
        disableRowSelectionOnClick
        paginationModel={{ page: 0, pageSize: 25 }}
        pageSizeOptions={[25]}
        rows={npsNotifications}
        columns={columns}
        onCellClick={(params) => {
          if (params.colDef.field !== 'edit') {
            const isDefault = params.row.isDefault as boolean;
            const notificationId = params.id as string;
            handleNaviagteToNpsNotification(notificationId, isDefault);
          }
        }}
      />
    </Box>
  );
};

export default NpsNotifications;
